import { useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => (slug ? getArticleBySlug(slug) : undefined), [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return getRelatedArticles(article.slug, article.category, 4);
  }, [article]);

  const category = useMemo(
    () => (article ? categories.find((c) => c.slug === article.category) : undefined),
    [article],
  );

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 黑料吃瓜网`;
    } else {
      document.title = '文章未找到 - 黑料吃瓜网';
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-2xl text-foreground-950 mb-4">文章未找到</h1>
          <p className="text-foreground-600">该文章不存在或已被移除。</p>
          <Link to="/" className="inline-block mt-4 text-primary-500 hover:text-primary-600 cursor-pointer">
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '黑料吃瓜网',
    },
    publisher: {
      '@type': 'Organization',
      name: '黑料吃瓜网',
      url: 'https://heiliaochigua.com',
    },
    url: `https://heiliaochigua.com/article/${article.slug}`,
    image: article.image,
    articleSection: category?.name || '',
    keywords: article.tags.join(','),
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <Navbar />
      <main>
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '分类', href: category ? `/category/${category.slug}` : undefined },
                { label: article.title },
              ]}
            />
          </div>

          <div className="mb-6">
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="inline-block bg-primary-100 text-primary-600 text-xs px-3 py-1 rounded-full mb-4 hover:bg-primary-200 transition-colors duration-200 cursor-pointer"
              >
                {category.name}
              </Link>
            )}
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 leading-snug mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 次阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-price-tag-3-line w-4 h-4 flex items-center justify-center"></i>
                {article.tags.join(' · ')}
              </span>
            </div>
          </div>

          <div className="relative aspect-[16/9] rounded-xl overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="article-content text-base leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/70">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-sm bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-12">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 mb-6">
                <i className="ri-links-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
                <h2 className="font-heading text-xl text-foreground-950">相关推荐</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group flex gap-4 bg-background-50 rounded-xl p-4 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                  >
                    <div className="w-32 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-heading text-sm text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                        {ra.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-2 text-xs text-foreground-500">
                        <span>{ra.date}</span>
                        <span>{ra.views >= 10000 ? `${(ra.views / 10000).toFixed(1)}万阅读` : `${ra.views}阅读`}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}